import {
  AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener,
  Inject, OnDestroy, PLATFORM_ID, ViewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { Router } from '@angular/router';
import { Modal } from '../ui/modal/modal';
import { ButtonComponent } from '../ui/button/button';
import { LabelComponent } from '../form/label/lable';
import { InputFieldComponent } from '../form/input/input-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { FormsModule } from '@angular/forms';
import { SearchSelectComponent } from '../form/search-select/search-select';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { AngularEditorConfig, AngularEditorModule } from '@kolkov/angular-editor';
import { EventService } from '../../shared/services/event.service';
import { PostService, PostAudienceType, IPost } from '../../shared/services/post.service';
import { UserService } from '../../shared/services/user.service';
import { ToastrService } from 'ngx-toastr';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MentionInputDirective, MentionUser } from '../../shared/directives/mention-input.directive';
import { MentionDropdownComponent } from '../ui/dropdown/mention-dropdown/mention-dropdown.component';
import { HashtagInputDirective } from '../../shared/directives/hashtag-input.directive';
import { HashtagDropdownComponent } from '../ui/dropdown/dropdown-item/hashtag-dropdown';
import { HashtagService, HashtagSuggestion } from '../../shared/services/hashtag.service';
import { CameraCaptureComponent, CapturedPhoto } from '../ui/camera-capture/camera-capture';
import { VideoCaptureComponent, CapturedVideo } from '../ui/video-capture/video-capture';
import { EditPostService, IEditPostRequest, IEditPostUser } from '../../shared/services/edit-post.service';
import { EditEventService, IEditEventRequest, IEditEventUser } from '../../shared/services/edit-event.service';
import { syncPacContainerVisibility } from '../../shared/utils/google-places.util';
import { isHeicFile, readAsPreviewDataUrl } from '../../shared/utils/heic.util';
import { EmojiPickerComponent } from '../ui/emoji-picker/emoji-picker';
import { LinkPreviewComponent } from '../ui/link-preview/link-preview';

const MAX_FILE_SIZE_MB = 20;
const MAX_POST_IMAGE_MB = 20;
const MAX_POST_VIDEO_MB = 50;
const MENTION_DEBOUNCE_MS = 200;

export interface PostMedia {
  file: File | null;        // null for saved (existing) items
  url: string | null;
  type: 'image' | 'video';
  id: string;
  previewProgress: number;
  isPreviewReady: boolean;
  savedId?: string;         // DB _id — present only for existing saved media
}

export interface PostPattern {
  id: string;
  name: string;
  bg: string;
  patternClass: string;
  color: string;
  textColor: string;
}

export interface PostUser {
  _id: string;
  displayName: string;
  userName: string;
  profileImage: { urlSmall?: string } | null;
  isFriend: boolean;
  /** Only set in edit mode — shows collaborator's current invitation status */
  collabStatus?: 'ACCEPTED' | 'PENDING' | 'DECLINED';
}

type EventFormField =
  | 'eventTitle'
  | 'startDateTime'
  | 'endDateTime'
  | 'genre'
  | 'location'
  | 'minPrice'
  | 'maxPrice'
  | 'bookingLink'
  | 'description'
  | 'images'
  | 'eventbriteLink';


declare const google: any;

/**
 * The "Create post" and "Create event" dialogs (plus their edit modes and the
 * camera / video capture they open). Mounted once by each header
 * (AppHeader, AppDashboardHeader) — there is a single copy of these screens,
 * so a fix here applies everywhere they appear.
 *
 * Open them with openPostModal() / openEventModal(); edit mode is driven by
 * EditPostService / EditEventService, as before.
 */
@Component({
  selector: 'app-create-content-modals',
  imports: [
    CommonModule,
    Modal,
    FormsModule,
    ButtonComponent,
    LabelComponent,
    InputFieldComponent,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTimepickerModule,
    SearchSelectComponent,
    AngularEditorModule,
    DragDropModule,
    MentionInputDirective,
    MentionDropdownComponent,
    HashtagInputDirective,
    HashtagDropdownComponent,
    CameraCaptureComponent,
    VideoCaptureComponent,
    EmojiPickerComponent,
    LinkPreviewComponent,
  ],
  templateUrl: './create-content-modals.html',
})
export class CreateContentModals implements AfterViewInit, AfterViewChecked, OnDestroy {

  // ── EVENT MODAL STATE ────────────────────────────────────────────────────

  isEventOpen = false;
  activeTab: 'other' | 'eventBrite' = 'other';
  isSaving = false;
  saveError = '';
  saveSuccess = '';

  editEventId: string | null = null;
  isEditEventMode = false;
  /** Unified list — saved (existing) images have `savedId` set; new uploads have `file` set.
   *  Order in this array IS the display/save order — drag-to-reorder mutates it directly. */
  eventSelectedMedia: PostMedia[] = [];

  eventTitle = '';
  startDate: Date | null = null;
  startTime: Date | null = null;
  endDate: Date | null = null;
  endTime: Date | null = null;
  selectedGenre = '';
  locationText = '';
  minPrice = '';
  maxPrice = '';
  bookingLink = '';
  htmlContent = '';
  eventbriteLink = '';
  isDragging = false;
  fileError = '';
  eventUploadProgress = 0;
  private eventUploadProgressTimer: ReturnType<typeof setInterval> | null = null;
  hashtags: string[] = [];
  hashtagInput = '';
  ebHashtags: string[] = [];
  ebHashtagInput = '';
  message = '';
  eventFieldErrors: Partial<Record<EventFormField, string>> = {};

  private activeReaders = new Map<string, FileReader>();
  private videoBlobUrls = new Map<string, string>();

  showEventTagPeople = false;
  eventTagSearchInput = '';
  eventTagSearchResults: PostUser[] = [];
  eventTaggedPeople: PostUser[] = [];
  eventTagInputFocused = false;
  private eventTagSearch$ = new Subject<string>();

  showEventCollaborators = false;
  eventCollabSearchInput = '';
  eventCollabSearchResults: PostUser[] = [];
  eventCollaborators: PostUser[] = [];
  eventCollabInputFocused = false;
  private eventCollabSearch$ = new Subject<string>();

  showEbTagPeople = false;
  ebTagSearchInput = '';
  ebTagSearchResults: PostUser[] = [];
  ebTaggedPeople: PostUser[] = [];
  ebTagInputFocused = false;
  private ebTagSearch$ = new Subject<string>();

  showEbCollaborators = false;
  ebCollabSearchInput = '';
  ebCollabSearchResults: PostUser[] = [];
  ebCollaborators: PostUser[] = [];
  ebCollabInputFocused = false;
  private ebCollabSearch$ = new Subject<string>();


  genreOptions = [
    { value: 'Social / Meet & Greet',        label: 'Social / Meet & Greet' },
    { value: 'Munch',                        label: 'Munch' },
    { value: 'Discussion Group',             label: 'Discussion Group' },
    { value: 'Community Gathering',          label: 'Community Gathering' },
    { value: 'Workshop / Class',             label: 'Workshop / Class' },
    { value: 'Training Course',              label: 'Training Course' },
    { value: 'Talk / Speaker Event',         label: 'Talk / Speaker Event' },
    { value: 'Conference',                   label: 'Conference' },
    { value: 'Retreat',                      label: 'Retreat' },
    { value: 'Festival',                     label: 'Festival' },
    { value: 'Club Night',                   label: 'Club Night' },
    { value: 'Party',                        label: 'Party' },
    { value: 'Play Party',                   label: 'Play Party' },
    { value: 'Speed Dating',                 label: 'Speed Dating' },
    { value: 'Dating Event',                 label: 'Dating Event' },
    { value: 'Singles Event',                label: 'Singles Event' },
    { value: 'Couples Event',                label: 'Couples Event' },
    { value: 'Online Event',                 label: 'Online Event' },
    { value: 'Fundraiser / Charity Event',   label: 'Fundraiser / Charity Event' },
    { value: 'Wellness / Self-Development',  label: 'Wellness / Self-Development' },
    { value: 'Polyamory',                    label: 'Polyamory' },
    { value: 'Open Relationships',           label: 'Open Relationships' },
    { value: 'Ethical Non-Monogamy',         label: 'Ethical Non-Monogamy' },
    { value: 'Swingers',                     label: 'Swingers' },
    { value: 'BDSM',                         label: 'BDSM' },
    { value: 'Kink',                         label: 'Kink' },
    { value: 'Fetish',                       label: 'Fetish' },
    { value: 'Rope / Shibari',               label: 'Rope / Shibari' },
    { value: 'Dominance & Submission (D/s)', label: 'Dominance & Submission (D/s)' },
    { value: 'Master / Slave (M/s)',         label: 'Master / Slave (M/s)' },
    { value: 'Tantra',                       label: 'Tantra' },
    { value: 'Sacred Sexuality',             label: 'Sacred Sexuality' },
    { value: 'LGBTQ+',                       label: 'LGBTQ+' },
    { value: 'Gay Men',                      label: 'Gay Men' },
    { value: 'Lesbian',                      label: 'Lesbian' },
    { value: 'Bisexual',                     label: 'Bisexual' },
    { value: 'Trans & Non-Binary',           label: 'Trans & Non-Binary' },
    { value: 'Women Only',                   label: 'Women Only' },
    { value: 'Men Only',                     label: 'Men Only' },
    { value: 'Sober Event',                  label: 'Sober Event' },
    { value: 'Beginners Welcome',            label: 'Beginners Welcome' },
    { value: 'Accessibility Friendly',       label: 'Accessibility Friendly' },
    { value: 'Other',                        label: 'Other' },
  ];

  config: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '15rem',
    minHeight: '5rem',
    placeholder: 'Enter text here...',
    translate: 'no',
    defaultParagraphSeparator: 'p',
    defaultFontName: 'Arial',
    // Without this, @kolkov/angular-editor runs Angular's DomSanitizer on every
    // keystroke (and again when loading a description back in for editing).
    // Angular's sanitizer allowlist has no `style` attribute, so it silently
    // strips text-align (all four justify toolbar buttons rely on inline
    // style) before the value ever reaches htmlContent/the backend — while
    // bold/italic/lists/links survive since they use tags Angular does allow.
    // The details page already re-trusts this value via bypassSecurityTrustHtml
    // (see event-details.ts getSafeHtml), so double-sanitizing here only loses
    // formatting without adding any real protection.
    sanitize: false,
    toolbarHiddenButtons: [
      ['insertImage', 'insertVideo'],
      ['toggleEditorMode'],
    ],
  };

  // ── POST MODAL STATE ─────────────────────────────────────────────────────

  isPostOpen = false;
  postStep: 1 | 2 | 3 | 4 | 5 = 1;

  // Edit-post mode
  editPostId: string | null = null;
  isEditPostMode = false;
  // editExistingMedia removed — saved items live in postSelectedMedia with savedId set
  private needsCaptionInject = false;

  postSelectedMedia: PostMedia[] = [];
  postActiveMediaIndex = 0;
  isPostDragging = false;
  postFileError = '';
  postUploadProgress = 0;
  private postUploadProgressTimer: ReturnType<typeof setInterval> | null = null;

  selectedPostGradient = '';
  postPatterns: PostPattern[] = [
    // Plain solid backgrounds only (ids kept for backward compatibility with existing posts)
    { id: 'p-dots-pink', name: 'Red', bg: '#D31148', patternClass: '', color: '#D31148', textColor: 'white' },
    // { id: 'p-checks-purple', ... }   ← unchanged
    { id: 'p-triangles-dark', name: 'Black', bg: '#000000', patternClass: '', color: '#000000', textColor: 'white' },
    // ...rest of commented lines unchanged
  ];

  postCaption = '';
  isCaptionMentionOpen = false;
  captionMentionUsers: MentionUser[] = [];
  private mentionSearch$ = new Subject<string>();

  showPostTagPeople = false;
  tagSearchInput = '';
  tagSearchResults: PostUser[] = [];
  postTaggedPeople: PostUser[] = [];
  tagInputFocused = false;
  private tagSearch$ = new Subject<string>();

  showPostCollaborators = false;
  collabSearchInput = '';
  collabSearchResults: PostUser[] = [];
  postCollaborators: PostUser[] = [];
  collabInputFocused = false;
  private collabSearch$ = new Subject<string>();

  postHashtags: string[] = [];
  postHashtagInput = '';
  // Hashtag autocomplete state — event modal
  hashSuggestions:   HashtagSuggestion[] = [];
  hashDropOpen       = false;
  // Hashtag autocomplete state — eventbrite tab
  ebHashSuggestions: HashtagSuggestion[] = [];
  ebHashDropOpen     = false;
  // Hashtag autocomplete state — post modal
  postHashSuggestions: HashtagSuggestion[] = [];
  postHashDropOpen     = false;
  isPostSaving = false;
  postSaveError = '';
  postSaveSuccess = '';
  postConsentChecked = false;

  postAudience = 'EVERYONE';
  showPostAudience = false;
  postAudienceOptions = [
    { value: 'EVERYONE', label: 'Everyone', icon: '🌍', desc: 'Anyone on Safeword can see this' },
    { value: 'FOLLOWERS_FRIENDS', label: 'Followers + Friends', icon: '👥', desc: 'Only Followers and Friends can see this' },
    { value: 'FRIENDS_ONLY', label: 'Only Friends', icon: '⭐', desc: '' },
    { value: 'ONLY_ME', label: 'Only Me', icon: '🔒', desc: 'Just you' },
  ];

  postLocation = '';
  showPostLocation = false;
  postLocationSuggestions = ['London', 'New York', 'Paris', 'Tokyo', 'Dubai'];

  // ── VIEW CHILDREN ────────────────────────────────────────────────────────

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('locationInput') locationInput!: InputFieldComponent;
  @ViewChild('postLocationInput') postLocationInput?: ElementRef<HTMLInputElement>;
  @ViewChild('patternMentionDir') patternMentionDir?: MentionInputDirective;
  @ViewChild('captionMentionDir') captionMentionDir?: MentionInputDirective;
  @ViewChild('postHashDir') postHashDir?: HashtagInputDirective;
  @ViewChild('hashDir')     hashDir?:     HashtagInputDirective;
  @ViewChild('ebHashDir')   ebHashDir?:   HashtagInputDirective;

  private removeEditorMousedownListener?: () => void;
  private locationAutocomplete?: any;
  private postLocationAutocomplete?: any;

  // ── CONSTRUCTOR ──────────────────────────────────────────────────────────

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private eventService: EventService,
    private postService: PostService,
    private userService: UserService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private editPostService: EditPostService,
    private editEventService: EditEventService,
    private hashtagService: HashtagService,
  ) {
    this.mentionSearch$
      .pipe(debounceTime(MENTION_DEBOUNCE_MS), distinctUntilChanged())
      .subscribe(q => this.fetchMentionUsers(q, 'caption'));

    this.tagSearch$
      .pipe(debounceTime(MENTION_DEBOUNCE_MS), distinctUntilChanged())
      .subscribe(q => this.fetchTagUsers(q));

    this.collabSearch$
      .pipe(debounceTime(MENTION_DEBOUNCE_MS), distinctUntilChanged())
      .subscribe(q => this.fetchCollabUsers(q));

    this.eventTagSearch$
      .pipe(debounceTime(MENTION_DEBOUNCE_MS), distinctUntilChanged())
      .subscribe(q => this.fetchEventTagUsers(q));

    this.eventCollabSearch$
      .pipe(debounceTime(MENTION_DEBOUNCE_MS), distinctUntilChanged())
      .subscribe(q => this.fetchEventCollabUsers(q));

    this.ebTagSearch$
      .pipe(debounceTime(MENTION_DEBOUNCE_MS), distinctUntilChanged())
      .subscribe(q => this.fetchEbTagUsers(q));

    this.ebCollabSearch$
      .pipe(debounceTime(MENTION_DEBOUNCE_MS), distinctUntilChanged())
      .subscribe(q => this.fetchEbCollabUsers(q));

    this.editPostService.onEditRequest$.subscribe(req => {
      this.openEditPostModal(req);
    });

    this.editEventService.onEditRequest$.subscribe(req => {
      this.openEditEventModal(req);
    });
  }

  // ── LIFECYCLE ────────────────────────────────────────────────────────────

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (this.showPostTagPeople && !target.closest('[data-tag-section]')) {
      this.showPostTagPeople = false;
    }
    if (this.showPostCollaborators && !target.closest('[data-collab-section]')) {
      this.showPostCollaborators = false;
    }
    if (this.showEventTagPeople && !target.closest('[data-event-tag-section]')) {
      this.showEventTagPeople = false;
    }
    if (this.showEventCollaborators && !target.closest('[data-event-collab-section]')) {
      this.showEventCollaborators = false;
    }
    if (this.showEbTagPeople && !target.closest('[data-eb-tag-section]')) {
      this.showEbTagPeople = false;
    }
    if (this.showEbCollaborators && !target.closest('[data-eb-collab-section]')) {
      this.showEbCollaborators = false;
    }
    if (this.showCaptionEmojiPicker && !target.closest('[data-caption-emoji-section]')) {
      this.showCaptionEmojiPicker = false;
    }
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      // Renderer2.listen()/@HostListener('document:...') both go through Angular's
      // EventManager, which registers document/window-scoped listeners as passive —
      // silently swallowing preventDefault(). This handler needs to actually block
      // the browser's default focus-shift, so it's wired with a raw addEventListener
      // and passive explicitly set to false.
      const mousedownHandler = (e: Event) => this.preventEditorToolbarFocusLoss(e as MouseEvent);
      document.addEventListener('mousedown', mousedownHandler, { capture: true, passive: false });
      this.removeEditorMousedownListener = () =>
        document.removeEventListener('mousedown', mousedownHandler, { capture: true });
    }
  }

  /**
   * Injects caption text the moment step-3 DOM is available.
   * @ViewChild refs inside *ngIf="postStep === 3" are undefined until that step renders.
   * We use AfterViewChecked + a flag to catch the exact render cycle.
   */
  ngAfterViewChecked(): void {
    if (!this.needsCaptionInject || this.postStep !== 3) return;

    const hasMedia = this.postSelectedMedia.length > 0;
    const isGradient = !!this.selectedPostGradient && !hasMedia;

    // Fast path — ViewChild already resolved
    if (isGradient && this.patternMentionDir) {
      this.patternMentionDir.setText(this.postCaption);
      this.needsCaptionInject = false;
      return;
    }
    if (!isGradient && this.captionMentionDir) {
      this.captionMentionDir.setText(this.postCaption);
      this.needsCaptionInject = false;
      return;
    }

    // Fallback — query DOM directly (handles the first render cycle gap)
    const selector = isGradient
      ? '[appMentionInput][data-pattern-caption]'
      : '[appMentionInput][data-media-caption]';

    setTimeout(() => {
      if (!this.needsCaptionInject) return;
      const el = document.querySelector(selector) as HTMLElement | null;
      if (el && this.postCaption) {
        el.innerHTML = '';
        el.appendChild(document.createTextNode(this.postCaption));
        el.dispatchEvent(new Event('input', { bubbles: true }));
      }
      this.needsCaptionInject = false;
    }, 30);
  }

  ngOnDestroy() {
    this.removeEditorMousedownListener?.();
    this.clearUploadProgressTimer('event');
    this.clearUploadProgressTimer('post');
    this.abortAllReaders();
    this.mentionSearch$.complete();
    this.tagSearch$.complete();
    this.collabSearch$.complete();
    this.eventTagSearch$.complete();
    this.eventCollabSearch$.complete();
    this.ebTagSearch$.complete();
    this.ebCollabSearch$.complete();
  }

  // ── SHARED HELPERS ───────────────────────────────────────────────────────

  get hasPendingEventPreviews(): boolean { return this.eventSelectedMedia.some(img => !img.savedId && !img.isPreviewReady); }
  get hasPendingPostPreviews(): boolean { return this.postSelectedMedia.some(m => !m.savedId && !m.isPreviewReady); }
  get eventPreviewProgress(): number { return this.calculateAverageProgress(this.eventSelectedMedia); }
  get postPreviewProgress(): number { return this.calculateAverageProgress(this.postSelectedMedia); }

  private calculateAverageProgress(items: Array<{ previewProgress: number }>): number {
    if (!items.length) return 0;
    return Math.round(items.reduce((sum, i) => sum + i.previewProgress, 0) / items.length);
  }

  formatFileSize(bytes: number): string {
    const mb = bytes / (1024 * 1024);
    return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
  }

  // ── FILE PREVIEW ─────────────────────────────────────────────────────────

  private abortAllReaders(): void {
    this.activeReaders.forEach(reader => { try { reader.abort(); } catch { } });
    this.activeReaders.clear();
    this.videoBlobUrls.forEach(url => URL.revokeObjectURL(url));
    this.videoBlobUrls.clear();
  }

  private abortReaderForItem(itemId: string): void {
    const reader = this.activeReaders.get(itemId);
    if (reader) { try { reader.abort(); } catch { } this.activeReaders.delete(itemId); }
    const blobUrl = this.videoBlobUrls.get(itemId);
    if (blobUrl) { URL.revokeObjectURL(blobUrl); this.videoBlobUrls.delete(itemId); }
  }

  private readFilePreview(item: PostMedia, target: 'event' | 'post'): void {
    this.abortReaderForItem(item.id);

    // Videos: use createObjectURL — instant preview, no base64 memory bloat
    if (item.type === 'video' && item.file) {
      const blobUrl = URL.createObjectURL(item.file);
      this.videoBlobUrls.set(item.id, blobUrl);
      item.url = blobUrl;
      item.previewProgress = 100;
      item.isPreviewReady = true;
      this.cdr.markForCheck();
      return;
    }

    // Images: read as DataURL for preview
    item.previewProgress = 5;
    item.isPreviewReady = false;
    this.cdr.markForCheck();

    let simProgress = 5;
    const SIM_TARGET = 85;
    const SIM_INTERVAL_MS = 80;
    const fileSizeMB = item.file!.size / (1024 * 1024);
    const simStep = fileSizeMB > 15 ? 1 : fileSizeMB > 5 ? 2 : 4;

    const simTimer = setInterval(() => {
      if (simProgress >= SIM_TARGET) { clearInterval(simTimer); return; }
      simProgress = Math.min(simProgress + simStep, SIM_TARGET);
      if (item.previewProgress < simProgress) { item.previewProgress = simProgress; this.cdr.markForCheck(); }
    }, SIM_INTERVAL_MS);

    const reader = new FileReader();
    this.activeReaders.set(item.id, reader);

    reader.onprogress = (event: ProgressEvent<FileReader>) => {
      if (event.lengthComputable && event.total > 0) {
        const realPct = Math.round((event.loaded / event.total) * 90);
        if (realPct > item.previewProgress) { item.previewProgress = realPct; simProgress = realPct; this.cdr.markForCheck(); }
      }
    };

    reader.onload = (event: ProgressEvent<FileReader>) => {
      clearInterval(simTimer);
      this.activeReaders.delete(item.id);
      item.url = (event.target?.result as string) ?? null;
      item.previewProgress = 100;
      item.isPreviewReady = true;
      this.cdr.markForCheck();
    };

    reader.onerror = () => {
      clearInterval(simTimer);
      this.activeReaders.delete(item.id);
      item.previewProgress = 0;
      item.isPreviewReady = false;
      if (target === 'post') {
        this.postSelectedMedia = this.postSelectedMedia.filter(m => m.id !== item.id);
        this.postFileError = `Couldn't read "${item.file!.name}". Please try another file.`;
      } else {
        this.eventSelectedMedia = this.eventSelectedMedia.filter(m => m.id !== item.id);
        this.fileError = `Couldn't read "${item.file!.name}". Please try another image.`;
      }
      this.cdr.markForCheck();
    };

    reader.onabort = () => { clearInterval(simTimer); this.activeReaders.delete(item.id); };
    readAsPreviewDataUrl(reader, item.file!);
  }

  // ── UPLOAD PROGRESS ──────────────────────────────────────────────────────

  private startUploadProgress(target: 'event' | 'post'): void {
    this.clearUploadProgressTimer(target);
    if (target === 'event') this.eventUploadProgress = 0; else this.postUploadProgress = 0;
    this.cdr.markForCheck();
    setTimeout(() => { this._setUploadProgressInternal(target, 2); this._startSimTimer(target); }, 50);
  }

  private _startSimTimer(target: 'event' | 'post'): void {
    const timer = setInterval(() => {
      const current = target === 'event' ? this.eventUploadProgress : this.postUploadProgress;
      if (current >= 88) { this.clearUploadProgressTimer(target); return; }
      const step = current < 30 ? 3 : current < 60 ? 2 : current < 80 ? 1 : 0.5;
      this._setUploadProgressInternal(target, Math.min(current + step, 88));
    }, 200);
    if (target === 'event') this.eventUploadProgressTimer = timer;
    else this.postUploadProgressTimer = timer;
  }

  private setUploadProgress(target: 'event' | 'post', value: number): void {
    const current = target === 'event' ? this.eventUploadProgress : this.postUploadProgress;
    const capped = Math.min(Math.max(0, Math.round(value)), 95);
    if (capped > current) {
      this.clearUploadProgressTimer(target);
      this._setUploadProgressInternal(target, capped);
      if (capped < 88) this._startSimTimer(target);
    }
  }

  private _setUploadProgressInternal(target: 'event' | 'post', value: number): void {
    const rounded = Math.round(value * 10) / 10;
    if (target === 'event') this.eventUploadProgress = rounded; else this.postUploadProgress = rounded;
    this.cdr.markForCheck();
  }

  private completeUploadProgress(target: 'event' | 'post'): void {
    this.clearUploadProgressTimer(target);
    const finishAnimation = () => {
      const current = target === 'event' ? this.eventUploadProgress : this.postUploadProgress;
      if (current >= 100) { this.cdr.markForCheck(); return; }
      this._setUploadProgressInternal(target, Math.min(current + 5, 100));
      if ((target === 'event' ? this.eventUploadProgress : this.postUploadProgress) < 100) setTimeout(finishAnimation, 30);
    };
    finishAnimation();
  }

  private resetUploadProgress(target: 'event' | 'post'): void {
    this.clearUploadProgressTimer(target);
    if (target === 'event') this.eventUploadProgress = 0; else this.postUploadProgress = 0;
    this.cdr.markForCheck();
  }

  private clearUploadProgressTimer(target: 'event' | 'post'): void {
    const timer = target === 'event' ? this.eventUploadProgressTimer : this.postUploadProgressTimer;
    if (timer !== null) clearInterval(timer);
    if (target === 'event') this.eventUploadProgressTimer = null; else this.postUploadProgressTimer = null;
  }

  // ── EVENT MODAL ──────────────────────────────────────────────────────────

  openEventModal() {
    this.isEditEventMode = false;
    this.editEventId = null;
    this.isEventOpen = true;
    setTimeout(() => this.initLocationAutocomplete(), 100);
  }

  closeEventModal() {
    this.isEventOpen = false;
    this.isEditEventMode = false;
    this.editEventId = null;
    this.resetEventForm();
  }

  openEditEventModal(req: IEditEventRequest): void {
    this.resetEventForm();
    this.editEventId = req.eventId;
    this.isEditEventMode = true;
    this.eventTitle = req.title ?? '';
    this.locationText = req.location ?? '';
    this.minPrice = req.minPrice ?? '';
    this.maxPrice = req.maxPrice ?? '';
    this.selectedGenre = req.genreOfEvent ?? '';
    this.bookingLink = req.linkToBookingSite ?? '';
    this.htmlContent = req.description ?? '';
    this.hashtags = (req.hashtags ?? []).map(h => h.replace(/^#/, ''));
    if (req.startDate) { this.startDate = new Date(req.startDate); this.startTime = new Date(req.startDate); }
    if (req.endDate) { this.endDate = new Date(req.endDate); this.endTime = new Date(req.endDate); }
    if (req.isExternal) { this.activeTab = 'eventBrite'; this.eventbriteLink = req.externalLink ?? ''; }
    else { this.activeTab = 'other'; }

    // Pre-populate tagged people
    this.eventTaggedPeople = (req.taggedPeople ?? []).map((u: IEditEventUser) => ({
      _id: u._id, displayName: u.displayName, userName: u.userName,
      profileImage: u.profileImage, isFriend: false,
    }));
    this.showEventTagPeople = this.eventTaggedPeople.length > 0;

    // Pre-populate collaborators
    this.eventCollaborators = (req.collaborators ?? []).map((u: IEditEventUser) => ({
      _id: u._id, displayName: u.displayName, userName: u.userName,
      profileImage: u.profileImage, isFriend: false,
      collabStatus: u.collabStatus,
    }));
    this.showEventCollaborators = this.eventCollaborators.length > 0;

    // Pre-populate for EventBrite tab tagged/collabs too if external
    if (req.isExternal) {
      this.ebTaggedPeople = (req.taggedPeople ?? []).map((u: IEditEventUser) => ({
        _id: u._id, displayName: u.displayName, userName: u.userName,
        profileImage: u.profileImage, isFriend: false,
      }));
      this.ebCollaborators = (req.collaborators ?? []).map((u: IEditEventUser) => ({
        _id: u._id, displayName: u.displayName, userName: u.userName,
        profileImage: u.profileImage, isFriend: false,
        collabStatus: u.collabStatus,
      }));
      this.showEbTagPeople = this.ebTaggedPeople.length > 0;
      this.showEbCollaborators = this.ebCollaborators.length > 0;
      this.ebHashtags = (req.hashtags ?? []).map(h => h.replace(/^#/, ''));
    }

    // Pre-populate existing media (unified list; new uploads add on top, in display order)
    this.eventSelectedMedia = (req.existingMedia ?? []).map(m => ({
      file: null,
      url: m.urlLarge || m.urlMedium || m.url,
      type: m.type as 'image' | 'video',
      id: m._id,
      previewProgress: 100,
      isPreviewReady: true,
      savedId: m._id,
    }));

    this.isEventOpen = true;
    setTimeout(() => this.initLocationAutocomplete(), 100);
    this.cdr.markForCheck();
  }

  saveEditEvent(): void {
    if (!this.editEventId || this.isSaving) return;
    const isEB = this.activeTab === 'eventBrite';
    if (isEB ? !this.validateExternalEventForm() : !this.validateInternalEventForm()) return;
    const taggedPeople = isEB ? this.ebTaggedPeople : this.eventTaggedPeople;
    const collaborators = isEB ? this.ebCollaborators : this.eventCollaborators;
    const hashtagsStr = isEB ? this.ebHashtagsString : this.hashtagsString;

    const formData = new FormData();
    if (this.eventTitle) formData.append('title', this.eventTitle);
    if (!isEB) {
      if (this.startDate) formData.append('startDate', this.buildISODate(this.startDate, this.startTime));
      if (this.endDate) formData.append('endDate', this.buildISODate(this.endDate, this.endTime));
      formData.append('currency', 'GBP');
      if (this.minPrice) formData.append('minPrice', this.minPrice);
      if (this.maxPrice) formData.append('maxPrice', this.maxPrice);
      if (this.selectedGenre) formData.append('genreOfEvent', this.selectedGenre);
      if (this.locationText) formData.append('location', this.locationText);
      if (this.bookingLink) formData.append('linkToBookingSite', this.normalizeLink(this.bookingLink));
      if (this.htmlContent) formData.append('description', this.htmlContent);
    } else {
      if (this.eventbriteLink) formData.append('link', this.normalizeLink(this.eventbriteLink));
    }
    if (hashtagsStr) formData.append('hashtags', hashtagsStr);
    // Always append mentions/collaboratorIds — even as an empty string —
    // rather than only when non-empty. This is an edit, so an empty list
    // means "the user removed everyone"; omitting the field entirely would
    // make that indistinguishable from "field wasn't touched" and the last
    // tag/collaborator would never actually be removed.
    formData.append('mentions', taggedPeople.map(u => u._id).join(','));
    formData.append('collaboratorIds', collaborators.map(u => u._id).join(','));
    // Split the unified, user-reordered list: kept saved IDs (in new order) + new files (in order)
    const orderedSavedIds = this.eventSelectedMedia
      .filter(m => !!m.savedId)
      .map(m => m.savedId as string);
    const newFiles = this.eventSelectedMedia
      .filter(m => !m.savedId && !!m.file)
      .map(m => m.file as File);
    if (orderedSavedIds.length) formData.append('existingMediaIds', orderedSavedIds.join(','));
    for (const file of newFiles) formData.append('files', file);
    this.isSaving = true;
    this.saveError = '';
    this.eventService.updateEvent(this.editEventId, formData).subscribe({
      next: () => {
        setTimeout(() => {
          this.isSaving = false;
          this.toastr.success('Event updated successfully!');
          this.eventService.triggerRefresh();
          this.closeEventModal();
          this.cdr.markForCheck();
        }, 0);
      },
      error: (err: any) => {
        setTimeout(() => {
          this.isSaving = false;
          this.saveError = err?.error?.message ?? 'Failed to update event. Please try again.';
          this.cdr.markForCheck();
        }, 0);
      },
    });
  }

  private initLocationAutocomplete() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.locationInput?.inputRef?.nativeElement) return;
    try {
      const inputEl = this.locationInput.inputRef.nativeElement;
      if (!this.locationAutocomplete) {
        this.locationAutocomplete = new google.maps.places.Autocomplete(inputEl, { types: ['geocode'] });
        syncPacContainerVisibility();
      }
      google.maps.event.clearListeners(this.locationAutocomplete, 'place_changed');
      this.locationAutocomplete.addListener('place_changed', () => {
        const place = this.locationAutocomplete.getPlace();
        if (place.formatted_address) {
          this.locationText = place.formatted_address;
          inputEl.dispatchEvent(new Event('input'));
          inputEl.blur();
          this.hidePlacesAutocompletePanel();
        }
      });
    } catch (e) { console.warn('Google Autocomplete init failed:', e); }
  }

  private hidePlacesAutocompletePanel(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    window.setTimeout(() => {
      document.querySelectorAll('.pac-container').forEach(p => ((p as HTMLElement).style.display = 'none'));
    }, 0);
  }

  handleSave() {
    if (this.isSaving) return;
    this.saveError = '';
    this.saveSuccess = '';
    this.activeTab === 'eventBrite' ? this.saveExternalEvent() : this.saveInternalEvent();
  }

  private saveInternalEvent() {
    if (this.hasPendingEventPreviews) { this.saveError = 'Please wait while your event images finish preparing.'; return; }
    if (!this.validateInternalEventForm()) return;
    const formData = new FormData();
    formData.append('title', this.eventTitle);
    formData.append('startDate', this.buildISODate(this.startDate!, this.startTime));
    formData.append('endDate', this.buildISODate(this.endDate!, this.endTime));
    formData.append('currency', 'GBP');
    formData.append('minPrice', this.minPrice);
    formData.append('maxPrice', this.maxPrice);
    formData.append('genreOfEvent', this.selectedGenre);
    formData.append('location', this.locationText);
    formData.append('linkToBookingSite', this.normalizeLink(this.bookingLink));
    formData.append('description', this.htmlContent);
    formData.append('hashtags', this.hashtagsString);
    if (this.eventTaggedPeople.length) formData.append('mentions', this.eventTaggedPeople.map(u => u._id).join(','));
    if (this.eventCollaborators.length) formData.append('collaboratorIds', this.eventCollaborators.map(u => u._id).join(','));
    for (const img of this.eventSelectedMedia) if (img.file) formData.append('files', img.file);
    this.isSaving = true;
    this.startUploadProgress('event');
    this.eventService.createInternalEvent(formData).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          if (event.total && event.total > 0) this.setUploadProgress('event', Math.round((event.loaded / event.total) * 95));
          return;
        }
        if (event.type === HttpEventType.Response) {
          this.isSaving = false;
          this.completeUploadProgress('event');
          this.toastr.success(event.body?.message ?? 'Event created successfully!');
          setTimeout(() => {
            this.isEventOpen = false;
            this.resetEventForm();
            this.cdr.markForCheck();
            this.eventService.triggerRefresh();
            if (this.router.url !== '/events') this.router.navigate(['/events']);
          }, 400);
        }
      },
      error: (err) => { this.saveError = err?.error?.message ?? 'Failed to create event.'; this.isSaving = false; this.resetUploadProgress('event'); },
    });
  }

  private saveExternalEvent() {
    if (!this.validateExternalEventForm()) return;
    this.isSaving = true;
    this.startUploadProgress('event');
    const payload: any = { link: this.normalizeLink(this.eventbriteLink), hashtags: this.ebHashtagsString };
    if (this.ebTaggedPeople.length) payload.mentions = this.ebTaggedPeople.map(u => u._id).join(',');
    if (this.ebCollaborators.length) payload.collaboratorIds = this.ebCollaborators.map(u => u._id).join(',');
    this.eventService.createExternalEvent(payload).subscribe({
      next: (res: any) => {
        this.isSaving = false;
        this.completeUploadProgress('event');
        this.toastr.success(res?.message ?? 'Event imported successfully!');
        setTimeout(() => {
          this.isEventOpen = false;
          this.resetEventForm();
          this.cdr.markForCheck();
          this.eventService.triggerRefresh();
          if (this.router.url !== '/events') this.router.navigate(['/events']);
        }, 400);
      },
      error: (err) => { this.saveError = err?.error?.message ?? 'Failed to import event.'; this.isSaving = false; this.resetUploadProgress('event'); },
    });
  }

  private buildISODate(date: Date, time: Date | null): string {
    const d = new Date(date);
    if (time) d.setHours(time.getHours(), time.getMinutes(), 0, 0);
    return d.toISOString();
  }

  clearEventFieldError(field: EventFormField): void { delete this.eventFieldErrors[field]; }

  // @kolkov/angular-editor's toolbar buttons have no mousedown guard, so clicking
  // one (e.g. Bullet List) lets the browser's default action blur the contenteditable
  // and collapse its selection before the click handler's execCommand runs — the
  // next keystroke then lands at the start of the content instead of the cursor's
  // last position. Blocking the default action only for clicks outside the textarea
  // itself keeps normal text selection/editing intact while fixing toolbar buttons.
  // Wired up manually in ngAfterViewInit (see removeEditorMousedownListener) rather
  // than via @HostListener, because Angular's EventManager registers document/window
  // -scoped listeners as passive, which silently ignores preventDefault().
  preventEditorToolbarFocusLoss(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('.event-editor') && !target.closest('.angular-editor-textarea')) {
      event.preventDefault();
    }
  }

  private validateInternalEventForm(): boolean {
    const errors: Partial<Record<EventFormField, string>> = {};
    const title = this.eventTitle.trim();
    const location = this.locationText.trim();
    const bookingLink = this.bookingLink.trim();
    const description = this.getPlainTextFromHtml(this.htmlContent);
    const minPrice = this.minPrice.trim();
    const maxPrice = this.maxPrice.trim();

    if (!title) errors.eventTitle = 'Event title is required.';
    if (!this.startDate || !this.startTime) errors.startDateTime = 'Start date and time are required.';
    if (!this.endDate || !this.endTime) errors.endDateTime = 'End date and time are required.';

    const startDateTime = this.startDate && this.startTime ? new Date(this.buildISODate(this.startDate, this.startTime)) : null;
    const endDateTime = this.endDate && this.endTime ? new Date(this.buildISODate(this.endDate, this.endTime)) : null;
    if (!errors.startDateTime && !errors.endDateTime && startDateTime && endDateTime && endDateTime <= startDateTime) {
      errors.endDateTime = 'End date and time must be after start date and time.';
    }

    if (!this.selectedGenre) errors.genre = 'Category is required.';
    if (!location) errors.location = 'Location is required.';
    if (!minPrice) errors.minPrice = 'Min price is required.';
    if (!maxPrice) errors.maxPrice = 'Max price is required.';

    const minPriceValue = minPrice ? Number(minPrice) : null;
    const maxPriceValue = maxPrice ? Number(maxPrice) : null;
    if (minPrice && (minPriceValue === null || Number.isNaN(minPriceValue) || minPriceValue < 0)) errors.minPrice = 'Enter a valid min price.';
    if (maxPrice && (maxPriceValue === null || Number.isNaN(maxPriceValue) || maxPriceValue < 0)) errors.maxPrice = 'Enter a valid max price.';
    if (!errors.minPrice && !errors.maxPrice && minPriceValue !== null && maxPriceValue !== null && maxPriceValue < minPriceValue) errors.maxPrice = 'Max price must be ≥ min price.';
    if (!bookingLink) errors.bookingLink = 'Booking site link is required.';
    else if (!this.isValidUrl(bookingLink)) errors.bookingLink = 'Enter a valid booking site link, e.g. https://example.com or www.example.com.';
    if (!description) errors.description = 'Description is required.';
    if (!this.eventSelectedMedia.length) errors.images = 'Event image is required.';

    this.eventFieldErrors = errors;
    if (Object.keys(errors).length > 0) this.scrollToFirstEventError();
    return Object.keys(errors).length === 0;
  }

  private validateExternalEventForm(): boolean {
    const errors: Partial<Record<EventFormField, string>> = {};
    const eventbriteLink = this.eventbriteLink.trim();
    if (!eventbriteLink) errors.eventbriteLink = 'Eventbrite event link is required.';
    else if (!this.isValidUrl(eventbriteLink)) errors.eventbriteLink = 'Enter a valid Eventbrite event link.';
    this.eventFieldErrors = errors;
    if (Object.keys(errors).length > 0) this.scrollToFirstEventError();
    return Object.keys(errors).length === 0;
  }

  /** Field order matches the form's visual top-to-bottom layout — scrolls to
   *  whichever invalid/empty field appears first so the user isn't left
   *  staring at the submit button wondering why nothing happened. */
  private static readonly EVENT_FIELD_ORDER: EventFormField[] = [
    'eventTitle', 'startDateTime', 'endDateTime', 'genre', 'location',
    'minPrice', 'maxPrice', 'bookingLink', 'description', 'images', 'eventbriteLink',
  ];

  private scrollToFirstEventError(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const firstField = CreateContentModals.EVENT_FIELD_ORDER.find(f => this.eventFieldErrors[f]);
    if (!firstField) return;
    setTimeout(() => {
      document.getElementById(`event-field-${firstField}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  /** Hostname must have at least one dot and a 2+ letter TLD — `new URL()` on
   *  its own happily accepts "https://foo" and "https://www.", neither of which
   *  is a link anyone can open. */
  private static readonly HOSTNAME_RE =
    /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/i;

  /** Users type links both ways — "https://example.com" and a bare
   *  "www.example.com". Accept either and let `normalizeLink` add the scheme
   *  before it reaches the API, so what gets stored is always openable. */
  private normalizeLink(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) return '';
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  }

  private isValidUrl(value: string): boolean {
    try {
      const url = new URL(this.normalizeLink(value));
      if (!['http:', 'https:'].includes(url.protocol)) return false;
      return CreateContentModals.HOSTNAME_RE.test(url.hostname);
    } catch { return false; }
  }

  private getPlainTextFromHtml(value: string): string {
    if (!value) return '';
    return value.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
  }

  onDragOver(e: DragEvent) { e.preventDefault(); this.isDragging = true; }
  onDragLeave(e: DragEvent) { this.isDragging = false; }
  onDrop(e: DragEvent) { e.preventDefault(); this.isDragging = false; const files = e.dataTransfer?.files; if (files) this.handleFiles(Array.from(files)); }
  onFileSelected(event: Event) { const input = event.target as HTMLInputElement; if (input.files?.length) { this.handleFiles(Array.from(input.files)); input.value = ''; } }

  private static readonly ALLOWED_IMAGE_MIMES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
  private static readonly ALLOWED_IMAGE_EXT_RE = /\.(jpe?g|jfif|png|webp|heic|heif)$/i;
  /** `video/quicktime` matters: the iPhone camera records .mov, so every video
   *  picked from an iOS photo library arrives with that type. */
  private static readonly ALLOWED_VIDEO_MIMES = ['video/mp4', 'video/mpeg', 'video/webm', 'video/quicktime'];
  private static readonly ALLOWED_VIDEO_EXT_RE = /\.(mp4|mpe?g|webm|mov|qt)$/i;

  /** Some browsers/OSes report an empty or unrecognized mimetype for less
   *  common formats like .jfif (which is really just JPEG data) — fall back
   *  to the file extension in that case instead of rejecting a valid image. */
  private isAllowedImageFile(file: File): boolean {
    if (CreateContentModals.ALLOWED_IMAGE_MIMES.includes(file.type)) return true;
    // HEIC from Windows/Android pickers often has an empty or octet-stream type
    if (isHeicFile(file)) return true;
    if (!file.type) return CreateContentModals.ALLOWED_IMAGE_EXT_RE.test(file.name);
    return false;
  }

  private isAllowedVideoFile(file: File): boolean {
    if (CreateContentModals.ALLOWED_VIDEO_MIMES.includes(file.type)) return true;
    if (!file.type) return CreateContentModals.ALLOWED_VIDEO_EXT_RE.test(file.name);
    return false;
  }

  handleFiles(files: File[]) {
    this.fileError = '';
    for (const file of files) {
      if (!this.isAllowedImageFile(file)) {
        this.fileError = `"${file.name}" is not supported. Only JPG, JPEG, JFIF, PNG, WEBP, and HEIC files are allowed.`;
        continue;
      }
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > MAX_FILE_SIZE_MB + 0.001) {
        this.fileError = `"${file.name}" (${this.formatFileSize(file.size)}) exceeds the ${MAX_FILE_SIZE_MB}MB limit.`;
        continue;
      }
      const image: PostMedia = { file, url: null, type: 'image', id: `${Date.now()}-${Math.random()}`, previewProgress: 0, isPreviewReady: false };
      this.eventSelectedMedia.push(image);
      this.clearEventFieldError('images');
      this.readFilePreview(image, 'event');
    }
  }

  removeEventMedia(index: number) {
    const item = this.eventSelectedMedia[index];
    if (item && !item.savedId) this.abortReaderForItem(item.id);
    this.eventSelectedMedia.splice(index, 1);
  }

  get eventSavedMediaCount(): number { return this.eventSelectedMedia.filter(m => !!m.savedId).length; }
  get eventNewMediaCount(): number { return this.eventSelectedMedia.length - this.eventSavedMediaCount; }

  onEventMediaDrop(event: CdkDragDrop<PostMedia[]>) {
    moveItemInArray(this.eventSelectedMedia, event.previousIndex, event.currentIndex);
  }

  // ── HASHTAG AUTOCOMPLETE ────────────────────────────────────────────────

  onHashSearch(partial: string): void {
    this.hashtagService.searchHashtags(partial).subscribe(results => {
      this.hashSuggestions = results;
      this.cdr.markForCheck();
    });
  }
  onHashSelected(tag: HashtagSuggestion): void {
    const clean = tag.tag.replace(/^#+/, '');
    if (!this.hashtags.includes(clean)) this.hashtags.push(clean);
    if (this.hashDir) { this.hashDir.setText(''); this.hashDir.closeDropdown(); }
    this.hashtagInput    = '';
    this.hashDropOpen    = false;
    this.hashSuggestions = [];
  }

  onEbHashSearch(partial: string): void {
    this.hashtagService.searchHashtags(partial).subscribe(results => {
      this.ebHashSuggestions = results;
      this.cdr.markForCheck();
    });
  }
  onEbHashSelected(tag: HashtagSuggestion): void {
    const clean = tag.tag.replace(/^#+/, '');
    if (!this.ebHashtags.includes(clean)) this.ebHashtags.push(clean);
    if (this.ebHashDir) { this.ebHashDir.setText(''); this.ebHashDir.closeDropdown(); }
    this.ebHashtagInput    = '';
    this.ebHashDropOpen    = false;
    this.ebHashSuggestions = [];
  }

  onPostHashSearch(partial: string): void {
    this.hashtagService.searchHashtags(partial).subscribe(results => {
      this.postHashSuggestions = results;
      this.cdr.markForCheck();
    });
  }
  onPostHashSelected(tag: HashtagSuggestion): void {
    const clean = tag.tag.replace(/^#+/, '');
    if (!this.postHashtags.includes(clean)) this.postHashtags.push(clean);
    // Reset directive state (clears activeHashStart, closes dropdown)
    if (this.postHashDir) {
      this.postHashDir.setText('');
      this.postHashDir.closeDropdown();
    }
    this.postHashtagInput    = '';
    this.postHashDropOpen    = false;
    this.postHashSuggestions = [];
  }

  addHashtag(event: Event): void {
    event.preventDefault();
    const raw = this.hashtagInput.trim().replace(/^#+/, '').replace(/[,\s]+$/, '').trim();
    if (!raw || this.hashtags.includes(raw)) { this.hashtagInput = ''; return; }
    this.hashtags.push(raw); this.hashtagInput = '';
  }
  removeHashtag(tag: string) { this.hashtags = this.hashtags.filter(t => t !== tag); }
  get hashtagsString(): string { return this.hashtags.map(t => `#${t}`).join(' '); }

  addEbHashtag(event: Event): void {
    event.preventDefault();
    const raw = this.ebHashtagInput.trim().replace(/^#+/, '').replace(/[,\s]+$/, '').trim();
    if (!raw || this.ebHashtags.includes(raw)) { this.ebHashtagInput = ''; return; }
    this.ebHashtags.push(raw); this.ebHashtagInput = '';
  }
  removeEbHashtag(tag: string) { this.ebHashtags = this.ebHashtags.filter(t => t !== tag); }
  get ebHashtagsString(): string { return this.ebHashtags.map(t => `#${t}`).join(' '); }

  handleSelectGenreChange(value: string) { this.selectedGenre = value; }

  private resetEventForm() {
    this.eventSelectedMedia.forEach(m => { if (!m.savedId) this.abortReaderForItem(m.id); });
    this.clearUploadProgressTimer('event');
    this.eventTitle = ''; this.startDate = null; this.startTime = null;
    this.endDate = null; this.endTime = null;
    this.selectedGenre = ''; this.locationText = ''; this.minPrice = ''; this.maxPrice = '';
    this.bookingLink = ''; this.htmlContent = ''; this.eventSelectedMedia = [];
    this.hashtags = []; this.hashtagInput = ''; this.hashDropOpen = false; this.hashSuggestions = []; this.eventbriteLink = '';
    this.ebHashtags = []; this.ebHashtagInput = ''; this.ebHashDropOpen = false; this.ebHashSuggestions = []; this.fileError = '';
    this.eventUploadProgress = 0; this.eventFieldErrors = {};
    this.saveError = ''; this.saveSuccess = ''; this.isSaving = false;
    this.showEventTagPeople = false; this.eventTagSearchInput = ''; this.eventTagSearchResults = []; this.eventTaggedPeople = [];
    this.showEventCollaborators = false; this.eventCollabSearchInput = ''; this.eventCollabSearchResults = []; this.eventCollaborators = [];
    this.showEbTagPeople = false; this.ebTagSearchInput = ''; this.ebTagSearchResults = []; this.ebTaggedPeople = [];
    this.showEbCollaborators = false; this.ebCollabSearchInput = ''; this.ebCollabSearchResults = []; this.ebCollaborators = [];
  }

  // ── EVENT MODAL — tag people ─────────────────────────────────────────────

  toggleEventTagPeople(): void {
    this.showEventTagPeople = !this.showEventTagPeople;
    if (this.showEventTagPeople) { this.eventTagSearchInput = ''; this.eventTagSearchResults = []; this.fetchEventTagUsers(''); }
  }
  onEventTagSearchInput(query: string): void { this.eventTagSearch$.next(query); }
  onEventTagInputBlur(): void {
    // Delay so a (mousedown) selection on a result button — which fires before
    // blur — has already run addEventTag() before we hide the list.
    setTimeout(() => { this.eventTagInputFocused = false; }, 150);
  }
  private fetchEventTagUsers(query: string): void {
    this.userService.searchUsersForMention(query).subscribe({
      next: (res: any) => { const all: PostUser[] = (res?.data ?? []); const ids = new Set(this.eventTaggedPeople.map(u => u._id)); this.eventTagSearchResults = all.filter(u => !ids.has(u._id)); this.cdr.markForCheck(); },
      error: () => { this.eventTagSearchResults = []; },
    });
  }
  addEventTag(user: PostUser): void { if (this.eventTaggedPeople.find(u => u._id === user._id)) return; this.eventTaggedPeople.push(user); this.eventTagSearchResults = this.eventTagSearchResults.filter(u => u._id !== user._id); }
  removeEventTag(userId: string): void { this.eventTaggedPeople = this.eventTaggedPeople.filter(u => u._id !== userId); }

  // ── EVENT MODAL — collaborators ──────────────────────────────────────────

  toggleEventCollaborators(): void {
    this.showEventCollaborators = !this.showEventCollaborators;
    if (this.showEventCollaborators) { this.eventCollabSearchInput = ''; this.eventCollabSearchResults = []; this.fetchEventCollabUsers(''); }
  }
  onEventCollabSearchInput(query: string): void { this.eventCollabSearch$.next(query); }
  onEventCollabInputBlur(): void {
    // Delay so a (mousedown) selection on a result button — which fires before
    // blur — has already run addEventCollaborator() before we hide the list.
    setTimeout(() => { this.eventCollabInputFocused = false; }, 150);
  }
  private fetchEventCollabUsers(query: string): void {
    this.userService.searchUsersForCollaboration(query).subscribe({
      next: (res: any) => { const all: PostUser[] = (res?.data ?? []); const ids = new Set(this.eventCollaborators.map(u => u._id)); this.eventCollabSearchResults = all.filter(u => !ids.has(u._id)); this.cdr.markForCheck(); },
      error: () => { this.eventCollabSearchResults = []; },
    });
  }
  addEventCollaborator(user: PostUser): void { if (this.eventCollaborators.find(u => u._id === user._id)) return; this.eventCollaborators.push(user); this.eventCollabSearchResults = this.eventCollabSearchResults.filter(u => u._id !== user._id); }
  removeEventCollaborator(userId: string): void { this.eventCollaborators = this.eventCollaborators.filter(u => u._id !== userId); }

  // ── EVENTBRITE TAB — tag people ──────────────────────────────────────────

  toggleEbTagPeople(): void {
    this.showEbTagPeople = !this.showEbTagPeople;
    if (this.showEbTagPeople) { this.ebTagSearchInput = ''; this.ebTagSearchResults = []; this.fetchEbTagUsers(''); }
  }
  onEbTagSearchInput(query: string): void { this.ebTagSearch$.next(query); }
  onEbTagInputBlur(): void {
    setTimeout(() => { this.ebTagInputFocused = false; }, 150);
  }
  private fetchEbTagUsers(query: string): void {
    this.userService.searchUsersForMention(query).subscribe({
      next: (res: any) => { const all: PostUser[] = (res?.data ?? []); const ids = new Set(this.ebTaggedPeople.map(u => u._id)); this.ebTagSearchResults = all.filter(u => !ids.has(u._id)); this.cdr.markForCheck(); },
      error: () => { this.ebTagSearchResults = []; },
    });
  }
  addEbTag(user: PostUser): void { if (this.ebTaggedPeople.find(u => u._id === user._id)) return; this.ebTaggedPeople.push(user); this.ebTagSearchResults = this.ebTagSearchResults.filter(u => u._id !== user._id); }
  removeEbTag(userId: string): void { this.ebTaggedPeople = this.ebTaggedPeople.filter(u => u._id !== userId); }

  // ── EVENTBRITE TAB — collaborators ───────────────────────────────────────

  toggleEbCollaborators(): void {
    this.showEbCollaborators = !this.showEbCollaborators;
    if (this.showEbCollaborators) { this.ebCollabSearchInput = ''; this.ebCollabSearchResults = []; this.fetchEbCollabUsers(''); }
  }
  onEbCollabSearchInput(query: string): void { this.ebCollabSearch$.next(query); }
  onEbCollabInputBlur(): void {
    setTimeout(() => { this.ebCollabInputFocused = false; }, 150);
  }
  private fetchEbCollabUsers(query: string): void {
    this.userService.searchUsersForCollaboration(query).subscribe({
      next: (res: any) => { const all: PostUser[] = (res?.data ?? []); const ids = new Set(this.ebCollaborators.map(u => u._id)); this.ebCollabSearchResults = all.filter(u => !ids.has(u._id)); this.cdr.markForCheck(); },
      error: () => { this.ebCollabSearchResults = []; },
    });
  }
  addEbCollaborator(user: PostUser): void { if (this.ebCollaborators.find(u => u._id === user._id)) return; this.ebCollaborators.push(user); this.ebCollabSearchResults = this.ebCollabSearchResults.filter(u => u._id !== user._id); }
  removeEbCollaborator(userId: string): void { this.ebCollaborators = this.ebCollaborators.filter(u => u._id !== userId); }

  // ── POST MODAL — open / close / steps ────────────────────────────────────

  openPostModal() {
    this.isEditPostMode = false;
    this.editPostId = null;
    this.needsCaptionInject = false;
    this.resetPostForm();
    this.isPostOpen = true;
  }

  closePostModal() {
    this.isPostOpen = false;
    this.resetPostForm();
    this.isEditPostMode = false;
    this.editPostId = null;
    this.needsCaptionInject = false;
  }

  /** Opens the post modal pre-populated for editing — starts at step 1 like create */
  openEditPostModal(req: IEditPostRequest): void {
    this.resetPostForm();
    this.editPostId = req.postId;
    this.isEditPostMode = true;

    // Load existing saved media into the unified list (savedId marks them as saved)
    this.postSelectedMedia = (req.existingMedia ?? []).map(m => ({
      file: null,
      url: m.urlLarge || m.urlMedium || m.url,
      type: m.type as 'image' | 'video',
      id: m._id,
      previewProgress: 100,
      isPreviewReady: true,
      savedId: m._id,
    }));

    if (req.backgroundPattern && (req.existingMedia ?? []).length === 0) {
      this.selectedPostGradient = req.backgroundPattern;
    }

    this.postCaption = req.caption ?? '';
    this.postLocation = req.location ?? '';
    this.postAudience = req.audience ?? 'EVERYONE';
    this.postHashtags = (req.hashtags ?? []).map(h => h.replace(/^#/, ''));

    this.postTaggedPeople = (req.taggedPeople ?? []).map((u: IEditPostUser) => ({
      _id: u._id, displayName: u.displayName, userName: u.userName,
      profileImage: u.profileImage, isFriend: false,
    }));

    this.postCollaborators = (req.collaborators ?? []).map((u: IEditPostUser) => ({
      _id: u._id, displayName: u.displayName, userName: u.userName,
      profileImage: u.profileImage, isFriend: false,
      collabStatus: u.collabStatus,
    }));

    this.postStep = 1;
    this.needsCaptionInject = false;
    // Auto-expand collaborators section if there are existing ones
    this.showPostCollaborators = (req.collaborators ?? []).length > 0;
    this.isPostOpen = true;
    this.cdr.markForCheck();
  }

  /** Saves edits — sends ordered saved IDs + new files */
  saveEditPost(): void {
    if (!this.editPostId || this.isPostSaving) return;

    const isMediaPost = this.postSelectedMedia.length > 0;
    const isBackgroundPost = !!this.selectedPostGradient && !isMediaPost;

    if (isBackgroundPost && !this.postCaption.trim()) {
      this.postSaveError = 'Please add a caption for your background post.';
      return;
    }

    this.isPostSaving = true;
    this.postSaveError = '';
    const pattern = this.getSelectedPattern();

    // Split unified list: saved items (in user's order) and new File uploads
    const orderedSavedIds = this.postSelectedMedia
      .filter(m => !!m.savedId)
      .map(m => m.savedId as string);
    const newFiles = this.postSelectedMedia
      .filter(m => !m.savedId && !!m.file)
      .map(m => m.file as File);

    this.postService.updatePost(this.editPostId, {
      // Always send the caption (even '') on edit: leaving it out made the API
      // treat it as "unchanged", so a cleared caption was never saved.
      caption: this.postCaption.trim(),
      // Send '' (not undefined) when there's no pattern so the backend
      // actually clears backgroundPattern/backgroundBg on the post. The
      // update endpoint only touches a field when it's !== undefined, so
      // switching from a background post to a media post — where pattern
      // is now undefined — would otherwise leave the old background
      // sitting in the DB alongside the new media.
      backgroundPattern: pattern?.id || '',
      backgroundBg: pattern?.bg || '',
      location: this.postLocation || undefined,
      audience: (this.postAudience as PostAudienceType) || 'EVERYONE',
      hashtags: this.postHashtags.length ? this.postHashtags.map(t => `#${t}`).join(' ') : undefined,
      collaboratorIds: this.postCollaborators.map(u => u._id),
      mentions: this.postTaggedPeople.map(u => u._id),
      files: newFiles,
      // ordered kept media IDs; backend deletes unlisted, applies order.
      // Always sent (even []) so removing all media — e.g. switching to a
      // background post — actually clears them; see updatePost() in
      // post.service.ts for why `?.length` there was wrong.
      removedMediaIds: orderedSavedIds,
    }).subscribe({
      next: () => {
        setTimeout(() => {
          this.isPostSaving = false;
          this.closePostModal();
          this.postService.triggerRefresh();
          this.toastr.success('Post updated successfully!');
          this.cdr.markForCheck();
        }, 0);
      },
      error: (err: any) => {
        setTimeout(() => {
          this.isPostSaving = false;
          this.postSaveError = err?.error?.message ?? 'Failed to update post. Please try again.';
          this.cdr.markForCheck();
        }, 0);
      },
    });
  }

  goPostStep(step: 1 | 2 | 3 | 4 | 5) {
    if (this.hasPendingPostPreviews && this.postSelectedMedia.length > 0) {
      this.postFileError = 'Please wait while your media previews finish loading.';
      return;
    }
    const isGradientMode = !!this.selectedPostGradient && this.postSelectedMedia.length === 0;
    if (isGradientMode && step === 2) {
      this.postStep = this.postStep === 1 ? 3 : 1;
      if (this.postStep === 3) this.needsCaptionInject = true;
      return;
    }
    if (step === 2) this.postActiveMediaIndex = 0;
    this.postStep = step;
    if (step === 3) this.needsCaptionInject = true;
  }

  // ── POST MODAL — step 1: file upload ─────────────────────────────────────

  onPostDragOver(e: DragEvent) { e.preventDefault(); this.isPostDragging = true; }
  onPostDragLeave(e: DragEvent) { this.isPostDragging = false; }
  onPostDrop(e: DragEvent) { e.preventDefault(); this.isPostDragging = false; const files = e.dataTransfer?.files; if (files) this.handlePostFiles(Array.from(files)); }
  onPostFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files?.length ? Array.from(input.files) : [];
    // Clear unconditionally, and *before* processing. The input keeps the last
    // pick otherwise, and re-picking the same file fires no `change` event at
    // all — which is why a rejected video left the picker looking dead. Deferred
    // because assigning to `value` synchronously inside the change handler is
    // unreliable on iOS Safari.
    setTimeout(() => { input.value = ''; });
    if (files.length) this.handlePostFiles(files);
  }

  handlePostFiles(files: File[]) {
    this.postFileError = '';
    for (const file of files) {
      const isImage = this.isAllowedImageFile(file);
      const isVideo = this.isAllowedVideoFile(file);
      if (!isImage && !isVideo) { this.postFileError = `"${file.name}" is not supported. Please use JPG, JFIF, PNG, WEBP, HEIC, MP4, MOV, or WEBM.`; continue; }
      const limitMB = isImage ? MAX_POST_IMAGE_MB : MAX_POST_VIDEO_MB;
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > limitMB + 0.001) {
        this.postFileError = `${isImage ? 'Images' : 'Videos'} must be ${limitMB}MB or smaller — "${file.name}" is ${this.formatFileSize(file.size)}. Please choose a smaller file.`;
        continue;
      }
      this.selectedPostGradient = '';
      const media: PostMedia = { file, url: null, type: isImage ? 'image' : 'video', id: `${Date.now()}-${Math.random()}`, previewProgress: 0, isPreviewReady: false };
      this.postSelectedMedia.push(media);
      this.readFilePreview(media, 'post');
    }
  }

  removePostMedia(index: number) {
    const item = this.postSelectedMedia[index];
    if (item && !item.savedId) this.abortReaderForItem(item.id);
    this.postSelectedMedia.splice(index, 1);
    if (this.postSelectedMedia.length === 0 && this.postStep === 2) { this.postStep = 1; return; }
    if (this.postActiveMediaIndex >= this.postSelectedMedia.length)
      this.postActiveMediaIndex = Math.max(0, this.postSelectedMedia.length - 1);
  }

  // Alias — delegates to unified removePostMedia
  removeExistingPostMedia(index: number) {
    this.removePostMedia(index);
  }

  // ── POST MODAL — step 1: gradient picker ─────────────────────────────────

  selectPostPattern(p: PostPattern) {
    if (this.selectedPostGradient === p.id) { this.selectedPostGradient = ''; return; }
    this.selectedPostGradient = p.id;
    this.postSelectedMedia.forEach(m => this.abortReaderForItem(m.id));
    this.postSelectedMedia = [];
    this.postFileError = '';
  }

  clearPostPattern(): void {
    this.selectedPostGradient = '';
  }

  getSelectedPattern(): PostPattern | undefined { return this.postPatterns.find(p => p.id === this.selectedPostGradient); }

  // ── POST MODAL — step 2: drag-to-reorder ─────────────────────────────────

  setPostActiveMedia(index: number) { this.postActiveMediaIndex = index; }

  onPostMediaDrop(event: CdkDragDrop<PostMedia[]>) {
    moveItemInArray(this.postSelectedMedia, event.previousIndex, event.currentIndex);
    if (this.postActiveMediaIndex === event.previousIndex) this.postActiveMediaIndex = event.currentIndex;
    else if (event.previousIndex < event.currentIndex && this.postActiveMediaIndex > event.previousIndex && this.postActiveMediaIndex <= event.currentIndex) this.postActiveMediaIndex--;
    else if (event.previousIndex > event.currentIndex && this.postActiveMediaIndex < event.previousIndex && this.postActiveMediaIndex >= event.currentIndex) this.postActiveMediaIndex++;
  }

  onExistingPostMediaDrop(event: CdkDragDrop<PostMedia[]>) {
    this.onPostMediaDrop(event);
  }

  // ── POST MODAL — step 3: caption @mention ────────────────────────────────

  onCaptionMentionSearch(query: string): void { this.mentionSearch$.next(query); }
  onCaptionMentionDropdown(isOpen: boolean): void { this.isCaptionMentionOpen = isOpen; if (!isOpen) this.captionMentionUsers = []; }
  onCaptionMentionSelected(user: MentionUser): void { this.isCaptionMentionOpen = false; this.captionMentionUsers = []; }

  // ── POST MODAL — step 3: caption emoji picker ────────────────────────────
  showCaptionEmojiPicker = false;
  captionEmojiDDPos = { top: 0, left: 0 };
  private captionEmojiSavedRange: Range | null = null;

  toggleCaptionEmojiPicker(btn: HTMLElement): void {
    const willOpen = !this.showCaptionEmojiPicker;
    const sel = window.getSelection();
    if (sel?.rangeCount) this.captionEmojiSavedRange = sel.getRangeAt(0).cloneRange();
    this.showCaptionEmojiPicker = willOpen;
    if (willOpen) {
      const btnRect = btn.getBoundingClientRect();
      this.captionEmojiDDPos = { top: btnRect.bottom + 4, left: btnRect.left };
    }
  }

  pickCaptionEmoji(emoji: string, target: HTMLElement): void {
    // Native 'input' event from execCommand is picked up by appMentionInput's
    // own @HostListener('input'), which emits valueChange → postCaption —
    // no need to touch postCaption here.
    target.focus();
    if (this.captionEmojiSavedRange) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(this.captionEmojiSavedRange);
      this.captionEmojiSavedRange = null;
    }
    document.execCommand('insertText', false, emoji);
    this.showCaptionEmojiPicker = false;
  }

  private fetchMentionUsers(query: string, _context: 'caption'): void {
    this.userService.searchUsersForMention(query).subscribe({
      next: (res: any) => { this.captionMentionUsers = (res?.data ?? []) as MentionUser[]; this.cdr.markForCheck(); },
      error: () => { this.captionMentionUsers = []; },
    });
  }

  // ── POST MODAL — step 3: tag people ──────────────────────────────────────

  togglePostTagPeople(): void {
    this.showPostTagPeople = !this.showPostTagPeople;
    if (this.showPostTagPeople) { this.tagSearchInput = ''; this.tagSearchResults = []; this.fetchTagUsers(''); }
  }
  onTagSearchInput(query: string): void { this.tagSearch$.next(query); }
  onTagInputBlur(): void {
    setTimeout(() => { this.tagInputFocused = false; }, 150);
  }
  private fetchTagUsers(query: string): void {
    this.userService.searchUsersForMention(query).subscribe({
      next: (res: any) => { const all: PostUser[] = (res?.data ?? []); const ids = new Set(this.postTaggedPeople.map(u => u._id)); this.tagSearchResults = all.filter(u => !ids.has(u._id)); this.cdr.markForCheck(); },
      error: () => { this.tagSearchResults = []; },
    });
  }
  // Closing here — not just waiting on the input's own (blur) — is what makes
  // this reliable on touch. The old flow depended on selecting a row causing a
  // browser blur, which a 150ms timer then turned into `tagInputFocused =
  // false`. On iOS Safari that blur is not guaranteed to fire for a tap on a
  // non-input element, so the list could stay open — and because the tapped
  // row is removed from `tagSearchResults` right below, the rest of the list
  // reflows upward while a second tap is still in flight, landing it on
  // whatever the reflow left in that spot rather than the intended row. Ending
  // the selection by explicitly collapsing the list removes that window
  // entirely: it closes the instant a pick is made, on any input method, and
  // there's nothing left open for a stray tap to misfire into. Matches desktop,
  // where a fresh click on the search box is already required to add another.
  addPostTag(user: PostUser): void {
    if (this.postTaggedPeople.find(u => u._id === user._id)) return;
    this.postTaggedPeople.push(user);
    this.tagSearchResults = this.tagSearchResults.filter(u => u._id !== user._id);
    this.tagInputFocused = false;
  }
  removePostTag(userId: string): void { this.postTaggedPeople = this.postTaggedPeople.filter(u => u._id !== userId); }

  // ── POST MODAL — step 3: collaborators ───────────────────────────────────

  togglePostCollaborators(): void {
    this.showPostCollaborators = !this.showPostCollaborators;
    if (this.showPostCollaborators) { this.collabSearchInput = ''; this.collabSearchResults = []; this.fetchCollabUsers(''); }
  }
  onCollabSearchInput(query: string): void { this.collabSearch$.next(query); }
  onCollabInputBlur(): void {
    setTimeout(() => { this.collabInputFocused = false; }, 150);
  }
  private fetchCollabUsers(query: string): void {
    this.userService.searchUsersForCollaboration(query).subscribe({
      next: (res: any) => { const all: PostUser[] = (res?.data ?? []); const ids = new Set(this.postCollaborators.map(u => u._id)); this.collabSearchResults = all.filter(u => !ids.has(u._id)); this.cdr.markForCheck(); },
      error: () => { this.collabSearchResults = []; },
    });
  }
  // See addPostTag() above — same reasoning, same fix.
  addCollaborator(user: PostUser): void {
    if (this.postCollaborators.find(u => u._id === user._id)) return;
    this.postCollaborators.push(user);
    this.collabSearchResults = this.collabSearchResults.filter(u => u._id !== user._id);
    this.collabInputFocused = false;
  }
  removeCollaborator(userId: string): void { this.postCollaborators = this.postCollaborators.filter(u => u._id !== userId); }

  // ── POST MODAL — step 3: hashtags / audience / location ──────────────────

  addPostHashtag(event: Event): void {
    event.preventDefault();
    const raw = this.postHashtagInput.trim().replace(/^#+/, '').replace(/[,\s]+$/, '').trim();
    if (!raw || this.postHashtags.includes(raw)) { this.postHashtagInput = ''; return; }
    this.postHashtags.push(raw); this.postHashtagInput = '';
  }
  removePostHashtag(tag: string) { this.postHashtags = this.postHashtags.filter(t => t !== tag); }

  togglePostAudience() { this.showPostAudience = !this.showPostAudience; }
  selectPostAudience(val: string) { this.postAudience = val; this.showPostAudience = false; }

  togglePostLocation() {
    this.showPostLocation = !this.showPostLocation;
    if (this.showPostLocation) setTimeout(() => this.initPostLocationAutocomplete(), 100);
    else this.postLocationAutocomplete = undefined;
  }

  private initPostLocationAutocomplete() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.postLocationInput?.nativeElement) return;
    try {
      const inputEl = this.postLocationInput.nativeElement;
      if (!this.postLocationAutocomplete) {
        this.postLocationAutocomplete = new google.maps.places.Autocomplete(inputEl, { types: ['geocode'] });
        syncPacContainerVisibility();
      }
      google.maps.event.clearListeners(this.postLocationAutocomplete, 'place_changed');
      this.postLocationAutocomplete.addListener('place_changed', () => {
        const place = this.postLocationAutocomplete.getPlace();
        if (place.formatted_address) {
          this.postLocation = place.formatted_address;
          inputEl.dispatchEvent(new Event('input'));
          inputEl.blur();
          this.hidePlacesAutocompletePanel();
          this.cdr.markForCheck();
        }
      });
    } catch (e) { console.warn('Google Autocomplete init failed:', e); }
  }

  // ── POST MODAL — publish ─────────────────────────────────────────────────

  publishPost(): void {
    if (this.isPostSaving) return;
    this.postSaveError = '';
    if (!this.postConsentChecked) { this.postSaveError = 'You must certify consent before publishing.'; return; }
    const isMediaPost = this.postSelectedMedia.length > 0;
    if (!isMediaPost && !this.postCaption.trim()) { this.postSaveError = 'Please add a caption for your background post.'; return; }
    if (!isMediaPost && !this.selectedPostGradient) { this.postSaveError = 'Please select a media file or background.'; return; }
    if (isMediaPost && this.hasPendingPostPreviews) { this.postSaveError = 'Please wait while your media previews finish preparing.'; return; }

    const pattern = this.getSelectedPattern();
    this.isPostSaving = true;
    this.startUploadProgress('post');

    this.postService.createPost({
      caption: this.postCaption.trim() || undefined,
      backgroundPattern: pattern?.id || undefined,
      backgroundBg: pattern?.bg || undefined,
      location: this.postLocation || undefined,
      audience: (this.postAudience as PostAudienceType) || 'EVERYONE',
      hashtags: this.postHashtags.length ? this.postHashtags.map(t => `#${t}`).join(' ') : undefined,
      collaboratorIds: this.postCollaborators.length ? this.postCollaborators.map(u => u._id) : undefined,
      mentions: this.postTaggedPeople.length ? this.postTaggedPeople.map(u => u._id) : undefined,
      files: this.postSelectedMedia.filter(m => !!m.file).map(m => m.file as File),
    }).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          if (event.total && event.total > 0) this.setUploadProgress('post', Math.round((event.loaded / event.total) * 95));
          return;
        }
        if (event.type === HttpEventType.Response) {
          this.isPostSaving = false;
          this.completeUploadProgress('post');
          this.toastr.success(event.body?.message ?? 'Post published successfully!');
          this.postService.triggerRefresh();
          setTimeout(() => {
            this.closePostModal();
            this.cdr.markForCheck();
            if (this.router.url !== '/posts') this.router.navigate(['/posts']);
          }, 400);
        }
      },
      error: (err: any) => {
        this.isPostSaving = false;
        this.resetUploadProgress('post');
        this.postSaveError = err?.error?.message ?? 'Failed to publish post. Please try again.';
      },
    });
  }

  // ── POST MODAL — reset ───────────────────────────────────────────────────

  private resetPostForm(): void {
    this.postSelectedMedia.forEach(m => this.abortReaderForItem(m.id));
    this.clearUploadProgressTimer('post');
    this.postStep = 1; this.postSelectedMedia = []; this.postActiveMediaIndex = 0;
    this.selectedPostGradient = ''; this.isPostDragging = false;
    this.postFileError = ''; this.postUploadProgress = 0;
    this.postCaption = ''; this.isCaptionMentionOpen = false; this.captionMentionUsers = [];
    this.showPostTagPeople = false; this.tagSearchInput = ''; this.tagSearchResults = []; this.postTaggedPeople = [];
    this.showPostCollaborators = false; this.collabSearchInput = ''; this.collabSearchResults = []; this.postCollaborators = [];
    this.postHashtags = []; this.postHashtagInput = ''; this.postHashDropOpen = false; this.postHashSuggestions = [];
    this.isPostSaving = false; this.postSaveError = ''; this.postSaveSuccess = '';
    this.postConsentChecked = false;
    this.postAudience = 'EVERYONE'; this.showPostAudience = false;
    this.postLocation = ''; this.showPostLocation = false; this.postLocationAutocomplete = undefined;
  }

  // ── CAMERA ───────────────────────────────────────────────────────────────

  cameraTarget: 'event' | 'post' | null = null;
  get isCameraOpen(): boolean { return this.cameraTarget !== null; }
  openCameraForEvent(): void { this.cameraTarget = 'event'; }
  openCameraForPost(): void { this.cameraTarget = 'post'; }

  onPhotoCaptured(photo: CapturedPhoto): void {
    const target = this.cameraTarget;
    this.cameraTarget = null;
    this.cdr.markForCheck();
    if (target === 'event') this.addCapturedImageToEvent(photo);
    else if (target === 'post') this.addCapturedImageToPost(photo);
  }

  onCameraCancelled(): void { this.cameraTarget = null; this.cdr.markForCheck(); }

  private addCapturedImageToEvent(photo: CapturedPhoto): void {
    const image: PostMedia = { file: photo.file, url: photo.dataUrl, type: 'image', id: `camera-${Date.now()}-${Math.random()}`, previewProgress: 100, isPreviewReady: true };
    this.eventSelectedMedia.push(image);
    this.clearEventFieldError('images');
    this.cdr.markForCheck();
  }

  private addCapturedImageToPost(photo: CapturedPhoto): void {
    this.selectedPostGradient = '';
    const media: PostMedia = { file: photo.file, url: photo.dataUrl, type: 'image', id: `camera-${Date.now()}-${Math.random()}`, previewProgress: 100, isPreviewReady: true };
    this.postSelectedMedia.push(media);
    this.cdr.markForCheck();
  }

  // ── VIDEO CAPTURE ────────────────────────────────────────────────────────

  isVideoCaptureOpen = false;
  openVideoCaptureForPost(): void { this.isVideoCaptureOpen = true; }
  onVideoCaptured(video: CapturedVideo): void { this.isVideoCaptureOpen = false; this.cdr.markForCheck(); this.addCapturedVideoToPost(video); }
  onVideoCaptureCancelled(): void { this.isVideoCaptureOpen = false; this.cdr.markForCheck(); }

  private addCapturedVideoToPost(video: CapturedVideo): void {
    this.selectedPostGradient = '';
    const media: PostMedia = { file: video.file, url: video.previewUrl, type: 'video', id: `camera-video-${Date.now()}-${Math.random()}`, previewProgress: 100, isPreviewReady: true };
    this.postSelectedMedia.push(media);
    this.cdr.markForCheck();
  }
}
